import { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { StoreBuildModel } from '../../../../models/store-build';
import { snappingService } from '../../../../services/SnappingService';
import { EnvironmentModel } from '../../StoreBuilderDetailsView/environments';
import { SELECTION_COLOR, SELECTION_OPACITY } from '../constants';
import {
  addSelectionOverlay,
  createFixtureMesh,
  isBoxWithinFloorBounds,
} from '../meshOperations';
import { FixtureItem, ThreeRefs } from '../types';

interface UseDragAndDropProps {
  mountRef: React.RefObject<HTMLDivElement>;
  refs: ThreeRefs;
  build: StoreBuildModel | null;
  environment: EnvironmentModel | null;
  clearSelections: () => void;
  setSelectedMeshesForUI: React.Dispatch<React.SetStateAction<THREE.Mesh[]>>;
}

interface UseDragAndDropReturn {
  setDraggedItem: (item: FixtureItem | null) => void;
}

export const useDragAndDrop = ({
  mountRef,
  refs,
  build,
  environment,
  clearSelections,
  setSelectedMeshesForUI,
}: UseDragAndDropProps): UseDragAndDropReturn => {
  const draggedItemRef = useRef<FixtureItem | null>(null);

  const setDraggedItem = (item: FixtureItem | null) => {
    draggedItemRef.current = item;
  };

  // Handle drag and drop
  useEffect(() => {
    if (!build || !environment) return;

    const mountElement = mountRef.current;
    if (!mountElement) return;

    const handleCanvasDragOver = (e: DragEvent) => {
      e.preventDefault();
    };

    const handleCanvasDrop = (e: DragEvent) => {
      e.preventDefault();

      if (
        draggedItemRef.current &&
        refs.scene.current &&
        refs.camera.current &&
        refs.floorPlane.current
      ) {
        const rect = mountElement.getBoundingClientRect();
        const mouseX = ((e.clientX - rect.left) / rect.width) * 2 - 1;
        const mouseY = -((e.clientY - rect.top) / rect.height) * 2 + 1;

        const raycaster = new THREE.Raycaster();
        raycaster.setFromCamera({ x: mouseX, y: mouseY }, refs.camera.current);

        const intersects = raycaster.intersectObject(refs.floorPlane.current);

        if (intersects.length > 0) {
          const intersectionPoint = intersects[0].point;

          if (
            isBoxWithinFloorBounds(
              intersectionPoint,
              draggedItemRef.current.size.width,
              draggedItemRef.current.size.height,
              refs.floorPlane.current
            )
          ) {
            // Apply snapping to the intersection point before creating the mesh
            const snappedPosition =
              snappingService.snapToGrid(intersectionPoint);

            // Create mesh at the snapped position
            const mesh = createFixtureMesh(
              draggedItemRef.current,
              snappedPosition
            );

            // Add mesh to scene
            refs.scene.current.add(mesh);

            // Apply edge snapping
            snappingService.snapToEdge(mesh, refs.scene.current);

            // Check for collisions after snapping
            const overlappingObjects = snappingService.checkOverlap(
              mesh,
              refs.scene.current
            );

            if (overlappingObjects.length > 0) {
              console.warn(
                'Collision detected with:',
                overlappingObjects.map((o) => o.userData.name || 'unknown')
              );
              // Optionally, you could remove the mesh if it collides
              // refs.scene.current.remove(mesh);
              // return;
            }

            // Check if out of bounds after snapping
            if (
              snappingService.checkOutOfBounds(mesh, refs.floorPlane.current)
            ) {
              console.warn('Object placed out of bounds');
              // Optionally, you could remove the mesh if it's out of bounds
              // refs.scene.current.remove(mesh);
              // return;
            }

            // Update selection state
            clearSelections();
            refs.selectedMeshes.current = [mesh];
            setSelectedMeshesForUI([mesh]);
            addSelectionOverlay(
              mesh,
              refs.scene.current,
              SELECTION_COLOR,
              SELECTION_OPACITY
            );

            // Update alignment lines after placing a new object
            snappingService.updateAlignmentLines(refs.scene.current);
          }
        }

        draggedItemRef.current = null;
      }
    };

    mountElement.addEventListener('dragover', handleCanvasDragOver);
    mountElement.addEventListener('drop', handleCanvasDrop);

    return () => {
      mountElement.removeEventListener('dragover', handleCanvasDragOver);
      mountElement.removeEventListener('drop', handleCanvasDrop);
    };
  }, [
    mountRef,
    refs.camera,
    refs.floorPlane,
    refs.scene,
    refs.selectedMeshes,
    build,
    environment,
    clearSelections,
    setSelectedMeshesForUI,
  ]);

  return {
    setDraggedItem,
  };
};
